import React from "react";
import clsx from "clsx";
import Card from "../Card/Card";
import type { CardOwnProps } from "../Card/Card";
import Modal from "../Modal/Modal";
import type { ModalOwnProps } from "../Modal/Modal";
import { useDynamicStore } from "../../../store/useDynamicStore";

// Helper to remove unwanted props
function filterCardProps(props: Record<string, unknown>) {
  const blacklist = [
    "formPropsOverride",
    "fieldPropsOverride",
    "columnsConfig",
    "collectionName",
    "onDataChange",
    "component",
    "props",
    "config",
    "mode",
    "theme",
    "data",
    "children",
    "body",
    "title",
    // Add more as needed
  ];
  const result: Record<string, unknown> = {};
  for (const key in props) {
    if (!blacklist.includes(key)) {
      result[key] = props[key];
    }
  }
  return result;
}

const THEME_CLASSNAMES = {
  light: "bg-white text-neutral-900",
  dark: "bg-neutral-900 text-neutral-100",
};

/**
 * DynamicRenderer
 * (docstring mantida)
 */
export interface DynamicRendererItem {
  id: string | number;
  type: "card" | "modal" | string;
  content: (Partial<CardOwnProps> & Partial<ModalOwnProps> & {
    body: React.ReactNode;
    [key: string]: unknown;
  });
  position?: {
    column?: string | number;
    row?: string | number;
  };
}

export interface DynamicRendererProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DynamicRendererItem[];
  columns?: number | string;
  rows?: number | string;
  theme?: "light" | "dark";
  componentMap?: {
    [type: string]: React.ComponentType<Record<string, unknown>>;
  };
  responsiveClassName?: string;
}

const DynamicRenderer: React.FC<DynamicRendererProps> = ({
  data,
  columns = 3,
  rows = 1,
  theme = "light",
  componentMap = {},
  responsiveClassName = "",
  ...rest
}) => {
  const { openModals, closeModal } = useDynamicStore();

  // Memoiza renderização dos cards
  const cards = React.useMemo(
    () =>
      data
        .map((item) => {
          if (item.type === "card") {
            const { title, body, className, style, ...rest } = item.content;
            const cardProps = filterCardProps(rest);
            return (
              <Card
                key={item.id}
                className={clsx("max-w-[420px] w-full mx-auto mb-4", className)}
                style={{
                  ...(item.position && {
                    gridColumn: item.position.column,
                    gridRow: item.position.row,
                  }),
                  ...(style as React.CSSProperties),
                }}
                role="region"
                aria-label={typeof title === "string" ? title : "Card"}
                {...cardProps}
              >
                {body}
              </Card>
            );
          }
          return null;
        })
        .filter(Boolean),
    [data]
  );

  // Memoiza renderização dos modals
  const modals = React.useMemo(
    () =>
      data
        .map((item, index) => {
          if (item.type === "modal") {
            const { open, onClose, title, body, ...modalProps } = item.content;
            return (
              <Modal
                key={item.id}
                open={typeof open === "boolean" ? open : openModals[index] ?? false}
                onClose={onClose || (() => closeModal(index))}
                header={title as React.ReactNode}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === "string" ? title : "Modal"}
                {...modalProps}
              >
                {body}
              </Modal>
            );
          }
          return null;
        })
        .filter(Boolean),
    [data, openModals, closeModal]
  );

  // Memoiza renderização dos widgets customizados
  const customWidgets = React.useMemo(
    () =>
      data
        .map((item) => {
          if (
            item.type !== "card" &&
            item.type !== "modal" &&
            componentMap[item.type]
          ) {
            const Widget = React.memo(componentMap[item.type]);
            // Optionally filter props here as well if needed
            return (
              <Widget
                key={item.id}
                {...item.content}
                style={{
                  ...(item.position && {
                    gridColumn: item.position.column,
                    gridRow: item.position.row,
                  }),
                  ...(item.content.style as React.CSSProperties),
                }}
              />
            );
          } else if (
            item.type !== "card" &&
            item.type !== "modal" &&
            !componentMap[item.type]
          ) {
            return (
              <div
                key={item.id}
                className={clsx(
                  "border border-dashed border-gray-400 p-4 text-gray-500 bg-gray-50 rounded",
                  typeof item.position?.column === "number" && `col-start-${item.position.column}`,
                  typeof item.position?.row === "number" && `row-start-${item.position.row}`
                )}
              >
                Tipo não suportado: <b>{item.type}</b>
              </div>
            );
          }
          return null;
        })
        .filter(Boolean),
    [data, componentMap]
  );

  return (
    <>
      <div
        className={clsx(
          "dynamic-renderer",
          "grid gap-4",
          responsiveClassName,
          "fluent-background",
          THEME_CLASSNAMES[theme]
        )}
        style={{
          gridTemplateColumns:
            typeof columns === "string"
              ? columns
              : `repeat(${columns}, minmax(0, 1fr))`,
          gridTemplateRows:
            typeof rows === "string"
              ? rows
              : `repeat(${rows}, auto)`,
        }}
        {...rest}
      >
        {cards}
        {customWidgets}
      </div>
      {modals}
    </>
  );
};

export default DynamicRenderer;